import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus } from "lucide-react";

export default function Clients() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", buyer_entity: "", intended_use: "", financing_source: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setShowNew(false);
      setForm({ name: "", email: "", phone: "", buyer_entity: "", intended_use: "", financing_source: "" });
      load();
    } catch (err) {
      toast.error("Failed to add");
    }
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="kicker mb-2">Directory</div>
          <h1 className="text-4xl font-display">Clients & Buyers</h1>
        </div>
        <button className="btn-brass" onClick={() => setShowNew(!showNew)} data-testid="new-client-toggle">
          <Plus size={16} /> Add Client
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="grid-panel p-6 mb-6 space-y-4" data-testid="new-client-form">
          <div className="kicker">New Client</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["name", "Full Name", true, "text"],
              ["buyer_entity", "Buyer Entity", false, "text"],
              ["email", "Email", false, "email"],
              ["phone", "Phone", false, "text"],
              ["intended_use", "Intended Use", false, "text"],
              ["financing_source", "Financing Source", false, "text"],
            ].map(([k, l, req, type]) => (
              <div key={k}>
                <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{l}</label>
                <input
                  type={type} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`client-field-${k}`}
                  className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowNew(false)} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-brass" data-testid="save-client-btn">Save Client</button>
          </div>
        </form>
      )}

      <div className="grid-panel">
        {loading ? (
          <div className="p-10 text-sm text-slate-500 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Users size={28} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">No clients yet. Add your first buyer to reuse across transactions.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-border">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Financing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.buyer_entity || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div>{c.email || "—"}</div>
                    <div className="text-xs text-slate-500">{c.phone || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.financing_source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
